// Client-side gzip for large JSON request bodies. A ~57k-task schedule commit
// is a ~90+ MB JSON POST, which the Cloudflare front of the RDrive API rejects
// with 413 — gzipped it is a few MB and sails through. Uses the browser's
// native CompressionStream (no dependency); the server side is ASP.NET Core
// request decompression, which keys off the Content-Encoding header.
//
// Opt-in per browser via the "GZip Requests" checkbox in the Settings dialog
// (preferences store, default off). Request decompression is not deployed to
// every RDrive API server yet: a gzipped commit sent to one without it reaches
// the JSON binder as raw bytes and 400s with "'0x1F' is an invalid start of a
// value" — the gzip magic byte — which no client-side validation can catch.

import { usePreferencesStore } from '@/stores/preferences';

export const DEFAULT_COMPRESS_THRESHOLD_BYTES = 1_048_576; // 1 MiB

export function commitGzipEnabled() {
  return usePreferencesStore().gzipRequests === true;
}

/**
 * Prepare a JSON request body, gzipping it when it is large enough to be worth
 * it (and allowed to be).
 *
 * @param {string} jsonString                The body, already JSON.stringify'd.
 * @param {Object}  [opts]
 * @param {number}  [opts.thresholdBytes]    Compress at or above this UTF-8 byte
 *                                           length (default 1 MiB).
 * @param {boolean} [opts.enabled]           Override the GZip Requests preference.
 * @returns {Promise<{ body: string | Uint8Array, headers: Object }>}
 *   Compressed: bytes + { 'Content-Encoding': 'gzip', 'Content-Type': 'application/json' }.
 *   Plain: the original string + { 'Content-Type': 'application/json' }.
 */
export async function prepareJsonBody(jsonString, {
  thresholdBytes = DEFAULT_COMPRESS_THRESHOLD_BYTES,
  enabled = commitGzipEnabled(),
} = {}) {
  const plain = { body: jsonString, headers: { 'Content-Type': 'application/json' } };

  // No CompressionStream (old browser / locked-down embed) → plain body; the
  // request is then subject to the upstream size limit exactly as before.
  if (!enabled || typeof CompressionStream === 'undefined') return plain;

  const bytes = new TextEncoder().encode(jsonString);
  if (bytes.byteLength < thresholdBytes) return plain;

  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('gzip'));
  // Wrap the full arrayBuffer in a Uint8Array that views the ENTIRE buffer:
  // axios sends ArrayBufferView bodies as `data.buffer` (ignoring any view
  // offset/length), so the view must own its whole buffer to go out intact.
  const compressed = new Uint8Array(await new Response(stream).arrayBuffer());

  return {
    body: compressed,
    headers: { 'Content-Encoding': 'gzip', 'Content-Type': 'application/json' },
  };
}
